import { type DesignSystemIcons } from "design-system/types";
import type { RouteLocationRaw } from "vue-router";

import { type BasePaginationData } from "shared/components/base/BasePagination";
import type DateRange from "shared/helpers/DateRange";
import {
  type SortOptionField,
  type SortOptionOrder,
} from "shared/helpers/mentions";
import type StreamFilters from "shared/helpers/StreamFilters";
import type {
  ChartExportFormat,
  ChartVariant,
  SavedChartSetting,
} from "shared/modules/Charts/types";
import {
  type Favorite,
  type InsightsArchived,
  type InstantInsightsReport,
  type StreamReport,
} from "shared/resources";
import { type ReportParamsOptions } from "shared/services/report";
import { type SelectAllId } from "shared/types";
import { type FeatureKey } from "shared/types/features";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";
import { type PermissionKey } from "shared/types/permissions";
import { type Maybe, type Nullable } from "shared/types/utility";

export const RECENT_DASHBOARD_LIMIT = 5;

/** User-facing report format options  */
export type StreamReportFormat = "pdf" | "csv";

export type InsightsGenerateReportAction =
  | ChartExportFormat
  | "scheduledReport";

export interface BaseInsightsNavigationItem {
  active?: boolean;
  label: string;
  to: RouteLocationRaw;
}

export interface InsightsNavigationItem extends BaseInsightsNavigationItem {
  icon: DesignSystemIcons;
  id: string;
}

export enum InsightsAction {
  AddToList = "addToList",
  AIVisibilityProject = "ai_visibility_project",
  Archive = "archive",
  Delete = "delete",
  DeleteChart = "deleteChart",
  Duplicate = "duplicate",
  DuplicateChart = "duplicateChart",
  Edit = "edit",
  GeneratePDF = "generatePdf",
  GenerateScheduledReport = "generateScheduledReport",
  GenerateXlsx = "generateXlsx",
  Pause = "pause",
  Regenerate = "regenerate",
  Resume = "resume",
  Save = "save",
  SaveChart = "saveChart",
  Share = "share",
  Show = "show",
  Star = "star",
  Unarchive = "unarchive",
  Unstar = "unstar",
}

export interface InsightsDataResponse<T> {
  data: T;
  meta: {
    stats: {
      select_all: { ids: number[] };
      total: { count: number };
    };
  };
}

export interface BaseInsightsResponse<T> {
  data: T[];
  pagination: Required<BasePaginationData>;
  selectAllIds: SelectAllId[];
}

export type InsightsDashboardsResponse =
  BaseInsightsResponse<InstantInsightsReport>;

export type InsightsDownloadsResponse = BaseInsightsResponse<StreamReport>;

export type InsightsStarredResponse = BaseInsightsResponse<Favorite>;

export type InsightsArchivedResponse = BaseInsightsResponse<InsightsArchived>;

export interface InsightsReportAttributes {
  format: ChartExportFormat;
  hideKeyInsightsPages?: boolean;
  keyInsightsCustomRange?: DateRange;
  label?: string;
  language: string;
  organisationBrandId?: number;
  summary?: string;
}

export enum InsightsLandingPageType {
  Archived = "archived",
  Dashboards = "dashboards",
  Downloads = "downloads",
  Shared = "shared",
  Starred = "starred",
}

export type InsightsLandingPageItem =
  | InstantInsightsReport
  | StreamReport
  | Favorite
  | InsightsArchived;

export type InsightsLandingPageItemTargetString =
  | "InstantInsightsReport"
  | "StreamReport";

export type InsightsLandingPageItemTargetClass =
  | InstantInsightsReport
  | StreamReport;

export interface InsightsLandingPageItemTarget {
  id: number;
  type: InsightsLandingPageItemTargetString;
}

export interface InsightsLandingPageTypeMap extends Record<
  InsightsLandingPageType,
  InsightsLandingPageItem
> {
  [InsightsLandingPageType.Archived]: InsightsArchived;
  [InsightsLandingPageType.Dashboards]: InstantInsightsReport;
  [InsightsLandingPageType.Downloads]: StreamReport;
  [InsightsLandingPageType.Shared]: InstantInsightsReport;
  [InsightsLandingPageType.Starred]: Favorite;
}

export interface InsightsDashboardFormData {
  advancedFilters?: Nullable<StreamFilters>;
  aiVisibilityProjectId?: number;
  dateRange: DateRange;
  description: string;
  newChartSettings: SavedChartSetting[];
  streamIds: number[];
  syndicationEnabled: boolean;
  title: string;
}

export interface InsightsDashboardError {
  buttonLabel?: string;
  description: string;
  title: string;
}

export interface InsightsDashboardProps {
  id: Maybe<number | string>;
}

export enum InsightsChartDetailTabName {
  AIVisibility = "ai-visibility",
  AIVisibilityAnswers = "ai-visibility-answers",
  Chart = "chart",
  ExportOptions = "export-options",
  Labels = "labels",
  MediaItems = "media-items",
  Settings = "settings",
  Social = "social",
  SocialItems = "social-items",
  SocialPosts = "social-posts",
}

export enum ChartClickEventType {
  AIVisibility = "aiVisibility",
  DataPoint = "datapoint",
  Dataset = "dataset",
  EmojiCount = "emojiCount",
  GeographicSpread = "geographicSpread",
  MentionBenchmarkTimeline = "mentionBenchmarkTimeline",
  TopFacebookPages = "topFacebookPages",
  TopTwitterInfluencers = "topTwitterInfluencers",
  WordCloud = "wordcloud",
}

export interface ChartClickQueryParams {
  chartVariant?: ChartVariant;
  dataIndex?: number;
  datasetIndex?: number;
  emoji?: string;
  eventType?: ChartClickEventType;
  linkedStreamId?: number;
  locationId?: number;
  sourceId?: number;
  streamId?: number;
  tab?: InsightsChartDetailTabName;
  word?: string;
}

export interface ChartExportParams<Options = ReportParamsOptions> {
  after: number;
  before: number;
  bundle_broadcast?: boolean;
  collapse_syndicated?: boolean;
  csv?: boolean;
  display_charts?: boolean;
  display_mentions?: boolean;
  excluded_items?: string[];
  excluded_syndication_keys?: string[];
  excluded_time_bundle_keys?: string[];
  filter_set_id?: number;
  format: string;
  include_print_clips_pdf?: boolean;
  label?: string;
  language?: string;
  note?: string;
  options?: Options;
  organisation_brand_id?: number;
  organisation_team_id?: number;
  pdf?: boolean;
  report_type?: string;
  show_restricted_content?: boolean;
  sort_by?: SortOptionField;
  sort_order?: SortOptionOrder;
  stock_symbol?: string;
  stream_id?: string | number;
  summary?: string;
  time_zone?: string;
  types?: (MentionType | SocialMentionType)[];
}

export interface PublicInsightsOrganisation {
  features: FeatureKey[];
}

export interface PublicInsightsUserResponse {
  organisation: PublicInsightsOrganisation;
  outreach_permissions: PermissionKey[];
  permissions: PermissionKey[];
  social_permissions: PermissionKey[];
}
